/**
 * CopyPushStep — Step 3 of CopyCampaignWorkflow ("Push"). Ported from
 * mock-push.html (changeset table, push options, gates rail, push modal +
 * progress list, receipt — still NO dry run and NO rollback, per that mock's
 * scope note).
 *
 * PUSH IS THE LAUNCH OF AN ALREADY-BUILT PLAN: the Adjust step's
 * planningMode builds created one campaigns row + one built launcher
 * template per source campaign, paused at orchestration status 'review'.
 * Pushing here fires POST /api/v2/campaigns/:id/planning-launch per build
 * (body {accountId, userId} — the orchestration metadata already carries
 * channelConfig/customerId/advertiserId from the build), which resumes the
 * orchestration into the platform's own API topic. The old payload
 * construction is GONE from this step — all of it happened at build time,
 * and what launches is exactly the SAVED template the Adjust step reviewed.
 *
 * Launch progress is polled on the SAME messageId POST /copy returned at
 * build time (the backend resumes that message's orchestration), against the
 * ORIGINAL TERMINAL_MESSAGE_STATUSES — 'review' is deliberately not terminal
 * here, it's what the message just left. Once every launch POST is accepted
 * this step navigates to the first new campaign's details page, whose
 * standard fast-track progress panel tracks the launch; a REJECTED POST
 * keeps the modal open so the error is read where it happened.
 *
 * Copies always land PAUSED (every copy launcher creates entities disabled/
 * draft — see copy_*_campaign_launcher.py), so the old Paused/Enabled
 * arrival-state radio was removed as decorative: it never reached a payload.
 * The DV360 merged-shell client guard is gone too — the backend rejects a
 * merged DV360 copy at BUILD time now, which step 3 surfaces as a failed
 * build with the real reason.
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  launchPlanningCampaign, fetchMessageStatus, TERMINAL_MESSAGE_STATUSES,
  PLATFORM_CHIP_CLASS, PLATFORM_CHIP_LABEL, copyStepLabels,
  isGoogleAdsCopyPlatform, formatCopyBudget } from './copyWorkflowApi';
import {
  campaignNamesFromLauncherResult, currencyFromLauncherResult,
} from './copyReviewEdits';
import { getPlatformName } from '../../utils/platformNames';
import CopyStepper from './CopyStepper';

// Presentation-only aggregate stages — the backend exposes coarse
// queued/processing/completed/failed per message, not per-stage launch
// progress, so advancement is driven by how many launches reached a terminal
// status. An honest approximation, same as before the rewrite.
const STAGES = [
  { l: 'Resuming N orchestrations', n: '×N' },
  { l: 'Creating campaigns in the platform', n: '×N' },
  { l: 'Creating ad & asset groups', n: '×N' },
  { l: 'Re-associating creatives', n: '×N' },
  { l: 'Finishing up', n: '—' },
];

export default function CopyPushStep({
  // Step rail labels — always the flow's 3 entries; defaulted so a caller that
  // doesn't pass them still renders the right rail.
  stepLabels = copyStepLabels(),
  platform, sourceCampaigns, builds, gatesByCampaign,
  onApplyUniqueName, interpretation, opOverrides, formFields,
  // (campaign) => the name this copy will be created under, resolved by
  // CopyCampaignWorkflow (typed name > pre-filled default). Used only as the label for a
  // build that has no template yet — still building, or failed — where the old fallback
  // announced `<source> (copy)`, a name the user had not chosen and the launch would not
  // have used. Defaulted so a caller that doesn't pass it keeps the old behaviour.
  intendedNameFor = (c) => `${c.name} (copy)`,
  accountId, userId, onBack, onPushComplete,
  // The platform account the copy lands in — a DV360/TTD advertiser id or a
  // Google Ads customer id. NOT the WowAI account: this card answers "where
  // does this go on the platform", and the tenant name ("mmgy-test") is not an
  // advertiser anyone can look up there.
  destinationAdvertiserId,
}) {
  const navigate = useNavigate();
  // Every source campaign has a build — the old picker-step excludedIds
  // pre-filter is gone (exclusion happened as row deletions on Adjust).
  const included = sourceCampaigns;

  // Per-source-campaign build rollup. The name shown/launched is the BUILT
  // template's own campaign name (grid edits and the unique-name fix already
  // wrote into it) — never a client-side guess the platform won't see.
  const buildRows = useMemo(() => included.map((c) => {
    const build = builds?.[c.id] || { status: 'building' };
    const gates = gatesByCampaign?.[c.id];
    const names = campaignNamesFromLauncherResult(build.launcherResult);
    return {
      sourceId: c.id,
      sourceName: c.name,
      build,
      gates,
      names: names.length ? names : [intendedNameFor(c)],
      blockedCount: gates?.blocked?.length || 0,
      launchable: build.status === 'review' && !build.alreadyLaunched
        && (gates?.blocked?.length || 0) === 0,
    };
  }), [included, builds, gatesByCampaign, intendedNameFor]);

  // The SOURCE ACCOUNT'S currency, off the first built template that states one. Read
  // from the BUILD rather than the source-campaign row because the row carries no
  // currency, and because the template is what the platform will actually be given.
  // Null (dv360/facebook, or an import that stated none) renders the legacy '$'.
  const builtCurrencyCode = useMemo(() => {
    for (const r of buildRows) {
      const code = currencyFromLauncherResult(r.build?.launcherResult);
      if (code) return code;
    }
    return null;
  }, [buildRows]);

  const launchable = buildRows.filter((r) => r.launchable);
  const stillBuilding = buildRows.filter((r) => r.build.status === 'building');
  const failedBuilds = buildRows.filter((r) => r.build.status === 'failed');
  const nBlocked = buildRows.reduce((n, r) => n + r.blockedCount, 0);
  const nNeed = buildRows.reduce((n, r) => n + (r.gates?.needs_decision?.length || 0), 0);
  const builtCampaignCount = launchable.reduce((n, r) => n + r.names.length, 0);

  const canPush = launchable.length > 0 && nBlocked === 0 && stillBuilding.length === 0;

  const [modalOpen, setModalOpen] = useState(false);
  const [pushing, setPushing] = useState(false);
  const [stageIndex, setStageIndex] = useState(0);
  // [{sourceId, campaignRowId, messageId, name, links, status, error}]
  const [results, setResults] = useState([]);
  const [pushError, setPushError] = useState(null);
  const pollTimers = useRef([]);

  useEffect(() => () => { pollTimers.current.forEach((t) => clearInterval(t)); }, []);

  const appliedOps = (interpretation?.ops || []).filter((op) => (
    Object.prototype.hasOwnProperty.call(opOverrides, op.id) ? opOverrides[op.id] : op.applied
  ));
  const declinedOps = (interpretation?.ops || []).filter((op) => !(
    Object.prototype.hasOwnProperty.call(opOverrides, op.id) ? opOverrides[op.id] : op.applied
  ));

  const allTerminal = results.length > 0 && results.every((r) => TERMINAL_MESSAGE_STATUSES.has(r.status));
  const anyFailed = results.some((r) => r.status === 'failed' || r.status === 'error');
  const doneCount = results.filter((r) => TERMINAL_MESSAGE_STATUSES.has(r.status)).length;
  const completedCount = results.filter((r) => r.status === 'completed').length;

  // Poll the ORIGINAL build messageId — planning-launch resumes that
  // message's orchestration, so its status is the launch's status. Carries
  // the failure REASON from `content` (e.g. "Fast track failed: …"), with the
  // persisted handler payload's own `error` as fallback, so the modal never
  // shows a bare "Failed: null".
  const pollOne = (result) => {
    const timer = setInterval(async () => {
      try {
        const status = await fetchMessageStatus(result.messageId, { accountId, userId });
        const failed = status.status === 'failed' || status.status === 'error';
        const reason = failed
          ? (status.content || status.result?.error || 'Launch failed — no reason reported')
          : null;
        setResults((prev) => prev.map((r) => (r.messageId === result.messageId
          ? { ...r, status: status.status, error: reason || r.error }
          : r)));
        if (TERMINAL_MESSAGE_STATUSES.has(status.status)) clearInterval(timer);
      } catch (err) {
        console.error('Error polling copy-launch message status', err);
      }
    }, 5000);
    pollTimers.current.push(timer);
  };

  const handlePush = async () => {
    if (!canPush) return;
    setModalOpen(true);
    setPushing(true);
    setPushError(null);
    setStageIndex(0);
    const seed = launchable.map((r) => ({
      sourceId: r.sourceId,
      campaignRowId: r.build.campaignRowId,
      messageId: r.build.messageId,
      links: r.build.links || null,
      name: r.names.join(' · '),
      status: 'pending',
      error: null,
    }));
    setResults(seed);

    const runOne = async (r) => {
      try {
        await launchPlanningCampaign(r.campaignRowId, { accountId, userId });
        setResults((prev) => prev.map((x) => (x.sourceId === r.sourceId
          ? { ...x, status: 'processing' } : x)));
        pollOne(r);
        return r.campaignRowId;
      } catch (err) {
        const msg = err.response?.data?.error || err.message || 'Launch failed';
        // Set the banner AND the row — a rejected POST is exactly the state
        // the old never-set pushError banner existed for.
        setPushError((prev) => prev || msg);
        setResults((prev) => prev.map((x) => (x.sourceId === r.sourceId
          ? { ...x, status: 'failed', error: msg } : x)));
        return null;
      }
    };

    setStageIndex(1);
    // Concurrent, always. This used to be a "Launch as one transaction"
    // toggle, which promised something it could not deliver: each campaign is
    // its own POST /planning-launch, so there is no transaction to be in and
    // nothing rolls back if a later one fails. All the switch ever chose was
    // concurrent vs one-at-a-time — over a flow that copies ONE campaign per
    // run, where both are the same thing.
    const launchedIds = await Promise.all(seed.map(runOne));
    setStageIndex(3);

    // Every launch ACCEPTED → the campaign detail page's standard fast-track
    // progress panel is the canonical place to watch from here. A REJECTED
    // launch keeps the modal open so the per-campaign error is read where the
    // failure happened.
    if (launchedIds.length > 0 && launchedIds.every(Boolean)) {
      navigate(`/campaigns/${launchedIds[0]}`);
    }
  };

  // Heuristic stage advancement from aggregate poll results (see STAGES).
  useEffect(() => {
    if (!pushing || results.length === 0) return;
    const fraction = doneCount / results.length;
    if (fraction >= 1) {
      setStageIndex(STAGES.length - 1);
      setPushing(false);
      onPushComplete?.(results);
    } else if (fraction > 0) {
      setStageIndex(3);
    }
  }, [results, doneCount, pushing, onPushComplete]);

  const handleDownloadReceipt = () => {
    const blob = new Blob([JSON.stringify({
      platform,
      launches: results.map((r) => ({
        name: r.name,
        platform,
        campaignRowId: r.campaignRowId,
        links: r.links,
        status: r.status,
        error: r.error,
      })),
    }, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `copy-campaign-receipt-${Date.now()}.json`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="cw-pane">
      <CopyStepper steps={stepLabels} current={stepLabels.indexOf('Push')} />
      <h1>Review and launch</h1>
      <p className="lede">
        Launching sends the plan you just reviewed to the platform. Copies always arrive
        <b> paused</b> — nothing runs until you turn them on there.
      </p>

      <div className="cw-cols">
        <div>
          <div className="cw-card">
            <div className="cw-card-h"><span className="cw-card-t">Changeset</span></div>
            <div className="cw-card-b flush">
              <table className="cw-ops-table">
                <tbody>
                  {buildRows.map((r) => (
                    <tr key={r.sourceId}>
                      <td><span className="op-badge op-badge-create">CREATE</span></td>
                      <td>
                        <span className={`ch ${PLATFORM_CHIP_CLASS[platform] || 'ch-srch'}`}>{PLATFORM_CHIP_LABEL[platform] || platform}</span>
                        <span className="op-obj">{r.names.join(' · ')}</span>
                        <div className="op-note">
                          from {r.sourceName}
                          {r.build.status === 'building' && ' — still building'}
                          {r.build.status === 'failed' && ` — build failed: ${r.build.error || 'unknown error'}`}
                          {r.build.status === 'review' && r.blockedCount > 0 && ` — ${r.blockedCount} blocked gate${r.blockedCount === 1 ? '' : 's'}`}
                        </div>
                      </td>
                      <td className="op-n">{r.names.length}</td>
                    </tr>
                  ))}
                  {appliedOps.map((op) => (
                    <tr key={op.id}>
                      <td><span className="op-badge op-badge-update">UPDATE</span></td>
                      <td><span className="op-obj">{op.label}</span></td>
                      <td className="op-n">{op.hits}</td>
                    </tr>
                  ))}
                  {declinedOps.map((op) => (
                    <tr key={op.id}>
                      <td><span className="op-badge op-badge-skip">SKIP</span></td>
                      <td><span className="op-obj">{op.label}</span><div className="op-note">declined on the describe step</div></td>
                      <td className="op-n">{op.hits}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          <div className="cw-card">
            <div className="cw-card-h"><span className="cw-card-t">Push options</span></div>
            <div className="cw-card-b">
              <div className="opt">
                <div style={{ flex: 1 }}>
                  <div className="opt-l">Arrival state</div>
                  <div className="opt-d">
                    Copies always land <b>paused</b> — every copy is built with its
                    entities disabled, so nothing can serve until you enable it in
                    the platform.
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="rail">
          <div className="cw-card">
            <div className="cw-card-h"><span className="cw-card-t">Gates</span></div>
            <div className="cw-card-b">
              <div className="gate-list">
                {nBlocked > 0 && <span className="gate gate-block"><span className="material-icons">block</span> {nBlocked} blocked</span>}
                {nNeed > 0 && <span className="gate gate-need"><span className="material-icons">help</span> {nNeed} need your decision</span>}
                {nBlocked === 0 && <span className="gate gate-ok"><span className="material-icons">check</span> nothing is blocking the launch</span>}
              </div>
              {buildRows.flatMap((r) => (r.gates?.blocked || []).map((b) => (
                <div className="gate-fix" key={`${r.sourceId}-${b.code}-${b.field}`}>
                  <div className="kv"><span>{b.code}</span><b>{b.message}</b></div>
                  {/* A duplicate name is the one blocker with an unambiguous
                      automatic fix — routed through the same applier +
                      planning-review save path the plan grid uses. */}
                  {b.code === 'duplicate_name' && onApplyUniqueName && (
                    <button
                      type="button"
                      className="cw-btn cw-btn-secondary cw-btn-sm"
                      onClick={() => onApplyUniqueName(r.sourceId)}
                    >
                      Use a unique name
                    </button>
                  )}
                </div>
              )))}
            </div>
          </div>
          <div className="cw-card">
            <div className="cw-card-h"><span className="cw-card-t">Destination</span></div>
            <div className="cw-card-b">
              <div className="kv">
                <span>{isGoogleAdsCopyPlatform(platform) ? 'Customer ID' : 'Advertiser ID'}</span>
                <b>{destinationAdvertiserId || '—'}</b>
              </div>
              <div className="kv"><span>Platform</span><b>{getPlatformName(platform)}</b></div>
              {/* Units are per-platform: DV360 copies are impression-budgeted,
                  everything else is currency — see formatCopyBudget. */}
              <div className="kv"><span>Budget</span><b>{formatCopyBudget(platform, formFields.budget, formFields.budgetType, '—', builtCurrencyCode)}</b></div>
              {/* A blank end date is a DELIBERATE state here (always-on), not a missing
                  value, so it reads as words rather than as the em-dash this panel uses
                  for "not set". */}
              <div className="kv"><span>Flight</span><b>{formFields.startDate || '—'} → {formFields.endDate || 'no end date'}</b></div>
            </div>
          </div>
          <div className="cw-card">
            <div className="cw-card-b">
              <button className="cw-btn cw-btn-push" onClick={handlePush} disabled={!canPush}>
                <span className="material-icons">rocket_launch</span> Launch {builtCampaignCount} built campaign{builtCampaignCount === 1 ? '' : 's'}
              </button>
              {!canPush && (
                <span className="fieldnote">
                  {stillBuilding.length > 0
                    ? 'Waiting for the build to finish on the Confirm & adjust step.'
                    : nBlocked > 0
                      ? 'Clear the blocked gates to enable.'
                      : failedBuilds.length > 0
                        ? 'Every build failed — retry them on the Confirm & adjust step.'
                        : 'Nothing is ready to launch.'}
                </span>
              )}
              <button className="cw-btn cw-btn-secondary cw-btn-sm" onClick={onBack}>Back to adjust</button>
            </div>
          </div>
        </div>
      </div>

      <div className={`cw-ov${modalOpen ? ' on' : ''}`}>
        <div className="cw-modal">
          <div className="cw-modal-h">
            <div className="cw-modal-t">{allTerminal ? (anyFailed ? 'Launch finished with errors' : 'Launched') : 'Launching…'}</div>
            <div className="cw-modal-s">
              {allTerminal
                ? `${completedCount} of ${results.length} campaigns completed`
                : `${doneCount} of ${results.length} campaigns done`}
            </div>
          </div>
          <div className="cw-modal-b">
            {!allTerminal && (
              <>
                <div className="prog">
                  <div className="prog-i" style={{ width: `${Math.round((stageIndex / (STAGES.length - 1)) * 100)}%` }} />
                </div>
                <div className="plist">
                  {STAGES.map((s, i) => (
                    <div key={s.l} className={`pitem${i === stageIndex ? ' on' : i < stageIndex ? ' done' : ''}`}>
                      <span className="material-icons">
                        {i < stageIndex ? 'check_circle' : i === stageIndex ? 'sync' : 'radio_button_unchecked'}
                      </span>
                      <span>{s.l.replace('N', String(results.length))}</span>
                      <span className="pitem-n">{s.n.replace('N', String(results.length))}</span>
                    </div>
                  ))}
                </div>
                <div className="receipt" style={{ marginTop: 10 }}>
                  {results.map((r) => (
                    <div className="kv" key={r.sourceId}>
                      <span>{r.name}</span>
                      <b className={r.status === 'failed' ? 'failed' : undefined}>
                        {r.status === 'failed' ? `Failed: ${r.error || 'no reason reported'}` : r.status}
                      </b>
                    </div>
                  ))}
                </div>
              </>
            )}
            {allTerminal && (
              <div>
                <div className={`succ-ic${anyFailed ? ' err' : ''}`}><span className="material-icons">{anyFailed ? 'error' : 'check'}</span></div>
                <p>{anyFailed
                  ? 'Some campaigns failed to launch — see details below.'
                  : `${results.length} campaign${results.length === 1 ? '' : 's'} launched to ${getPlatformName(platform)}, arriving paused.`}</p>
                <div className="receipt" style={{ marginTop: 10 }}>
                  {results.map((r) => (
                    <div className="kv" key={r.sourceId}>
                      <span>{r.name}</span>
                      <b className={r.status === 'failed' ? 'failed' : undefined}>
                        {r.status === 'failed' ? `Failed: ${r.error || 'no reason reported'}` : (
                          <a href={`/campaigns/${r.campaignRowId}`} onClick={() => navigate(`/campaigns/${r.campaignRowId}`)}>
                            {r.status}
                          </a>
                        )}
                      </b>
                    </div>
                  ))}
                  <div className="kv"><span>Linked source</span><b>{sourceCampaigns.map((c) => c.name).join(', ')}</b></div>
                </div>
              </div>
            )}
            {pushError && <div className="gate-banner" style={{ marginTop: 10 }}><span className="material-icons">error</span><div className="t">{pushError}</div></div>}
          </div>
          <div className="cw-modal-f">
            {allTerminal ? (
              <>
                <button className="cw-btn cw-btn-secondary cw-btn-sm" onClick={handleDownloadReceipt}>Download receipt</button>
                <button
                  className="cw-btn cw-btn-primary cw-btn-sm"
                  onClick={() => { const first = results.find((r) => r.status !== 'failed'); if (first) navigate(`/campaigns/${first.campaignRowId}`); }}
                  disabled={!results.some((r) => r.status !== 'failed')}
                >Open campaign</button>
                <button className="cw-btn cw-btn-secondary cw-btn-sm" onClick={() => setModalOpen(false)}>Close</button>
              </>
            ) : (
              <span className="fieldnote">Once this starts it can&apos;t be cancelled or undone.</span>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
